/**
 * QuickTrackDrawer — right-side slide-out panel that surfaces a birds-eye
 * view of a candidate's entire application journey. Fetches from
 * `GET /api/candidates/{id}/quick-track` — one call, four sections.
 *
 * Consumed by <TrackButton /> — drop that near any candidate name to
 * expose this drawer across candidate lists, kanban cards, and feed rows.
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { X, Sparkles, Wallet, MessageSquare, CalendarClock, Star,
    ThumbsUp, ThumbsDown, Mail, Phone, ChevronRight, Loader2,
    ArrowUpRight, Circle, FileSignature, Plus, Edit2, Trash2, Check, Pin } from "lucide-react";

/** Follow-up status enum — mirrors backend FOLLOWUP_STATUSES in
 *  routers/phase10.py. Kept as a module-level const so the quick-log form
 *  and the inline edit form share the same source of truth. */
const FOLLOWUP_STATUSES = [
    "Initial contact made",
    "Call back later",
    "Not reachable",
    "Switched off",
    "Call not received",
    "Unable to contact on WhatsApp",
    "Candidate not interested",
    "Interested — sent JD",
    "Other",
];
const FOLLOWUP_CHANNELS = ["call", "whatsapp", "email", "sms", "linkedin"];

function inr(v, currency = "INR") {
    if (v == null || v === "") return "—";
    try {
        return new Intl.NumberFormat("en-IN", {
            style: "currency", currency, maximumFractionDigits: 0,
        }).format(v);
    } catch {
        return `${v}`;
    }
}

function timeAgo(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    const min = Math.floor(diff / 60000);
    if (min < 1) return "just now";
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.floor(hr / 24);
    if (day < 30) return `${day}d ago`;
    return d.toLocaleDateString();
}

function KindIcon({ kind }) {
    const map = {
        note: MessageSquare,
        followup: Phone,
        email: Mail,
        sms: Phone,
        whatsapp: Phone,
        call: Phone,
        stage_change: ArrowUpRight,
    };
    const Icon = map[kind] || Circle;
    return <Icon className="w-3 h-3" />;
}

/** Pill styling for the follow-up status enum (mirrors phase10 FOLLOWUP_STATUSES).
 *  Positive intent (interested / initial contact) → green, neutral → slate,
 *  friction (unreachable / not interested) → amber/red. */
function followupStatusClasses(status) {
    const s = (status || "").toLowerCase();
    if (s.includes("interested") && !s.includes("not")) return "border-emerald-500 text-emerald-800 bg-emerald-50";
    if (s.includes("initial contact")) return "border-emerald-500 text-emerald-800 bg-emerald-50";
    if (s.includes("call back")) return "border-blue-400 text-blue-700 bg-blue-50";
    if (s.includes("not interested")) return "border-red-400 text-red-700 bg-red-50";
    if (s.includes("not reachable") || s.includes("switched off") || s.includes("not received") || s.includes("unable to contact")) return "border-amber-500 text-amber-800 bg-amber-50";
    return "border-border text-muted-foreground bg-neutral-50";
}

function Section({ title, icon: Icon, children, testId }) {
    return (
        <section className="border border-border bg-white" data-testid={testId}>
            <div className="px-4 py-2.5 border-b border-border bg-neutral-50 flex items-center gap-2">
                <Icon className="w-3.5 h-3.5 text-ikb" />
                <span className="text-[10px] font-mono uppercase tracking-widest">{title}</span>
            </div>
            <div className="p-4">{children}</div>
        </section>
    );
}

/** QuickNoteForm — compact textarea pinned at the top of the Follow-up
 *  history so a recruiter can jot a candidate note without leaving the
 *  drawer. Submits to POST /candidates/{cid}/notes and refetches. */
function QuickNoteForm({ candidateId, onDone }) {
    const [expanded, setExpanded] = useState(false);
    const [body, setBody] = useState("");
    const [busy, setBusy] = useState(false);

    async function submit(e) {
        e?.preventDefault?.();
        if (!candidateId || busy) return;
        const trimmed = body.trim();
        if (!trimmed) { toast.error("Note cannot be empty"); return; }
        setBusy(true);
        try {
            await api.post(`/candidates/${candidateId}/notes`, { body: trimmed });
            toast.success("Note added");
            setBody(""); setExpanded(false);
            onDone?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    if (!expanded) {
        return (
            <button
                type="button"
                onClick={() => setExpanded(true)}
                className="w-full mb-3 inline-flex items-center justify-center gap-2 h-9 border border-dashed border-emerald-500/60 text-emerald-800 hover:bg-emerald-500/5 text-[11px] font-mono uppercase tracking-widest"
                data-testid="qt-quicknote-open"
            >
                <Plus className="w-3.5 h-3.5" /> Quick add a note
            </button>
        );
    }

    return (
        <form
            onSubmit={submit}
            className="mb-3 border border-emerald-500/50 bg-emerald-500/5 p-3 space-y-2"
            data-testid="qt-quicknote-form"
        >
            <label className="block">
                <span className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground">Note</span>
                <textarea
                    autoFocus
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    rows={3}
                    placeholder="What do you want to remember about this candidate?"
                    className="mt-1 w-full border border-border bg-white text-xs px-2 py-1.5 resize-y"
                    data-testid="qt-quicknote-body"
                />
            </label>
            <div className="flex items-center gap-2 pt-1">
                <button
                    type="submit"
                    disabled={busy || !body.trim()}
                    className="inline-flex items-center gap-1 h-8 px-3 bg-emerald-700 text-white text-[11px] font-mono uppercase tracking-widest hover:bg-emerald-800 disabled:opacity-60"
                    data-testid="qt-quicknote-submit"
                >
                    {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                    Add note
                </button>
                <button
                    type="button"
                    onClick={() => { setExpanded(false); setBody(""); }}
                    className="h-8 px-3 border border-border text-[11px] font-mono uppercase tracking-widest bg-white hover:bg-neutral-50"
                    data-testid="qt-quicknote-cancel"
                >
                    Cancel
                </button>
            </div>
        </form>
    );
}

/** QuickFollowupForm — compact 1-row form pinned to the top of the
 *  Follow-up section so a recruiter can log a status change without
 *  leaving the drawer. Submits to POST /candidates/{cid}/followups and
 *  calls onDone() so the parent can refetch. */
function QuickFollowupForm({ candidateId, onDone }) {
    const [expanded, setExpanded] = useState(false);
    const [status, setStatus] = useState(FOLLOWUP_STATUSES[0]);
    const [channel, setChannel] = useState("call");
    const [note, setNote] = useState("");
    const [nextActionAt, setNextActionAt] = useState("");
    const [busy, setBusy] = useState(false);

    async function submit(e) {
        e?.preventDefault?.();
        if (!candidateId || busy) return;
        setBusy(true);
        try {
            const body = { status, channel, note: note.trim() };
            if (nextActionAt) body.next_action_at = new Date(nextActionAt).toISOString();
            await api.post(`/candidates/${candidateId}/followups`, body);
            toast.success("Follow-up logged");
            setNote(""); setNextActionAt(""); setExpanded(false);
            onDone?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    if (!expanded) {
        return (
            <button
                type="button"
                onClick={() => setExpanded(true)}
                className="w-full mb-3 inline-flex items-center justify-center gap-2 h-9 border border-dashed border-ikb/60 text-ikb hover:bg-ikb/5 text-[11px] font-mono uppercase tracking-widest"
                data-testid="qt-quicklog-open"
            >
                <Plus className="w-3.5 h-3.5" /> Quick log a follow-up
            </button>
        );
    }

    return (
        <form
            onSubmit={submit}
            className="mb-3 border border-ikb/40 bg-ikb/5 p-3 space-y-2"
            data-testid="qt-quicklog-form"
        >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <label className="block">
                    <span className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground">Status</span>
                    <select
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                        className="mt-1 w-full h-8 border border-border bg-white text-xs px-2"
                        data-testid="qt-quicklog-status"
                    >
                        {FOLLOWUP_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                </label>
                <label className="block">
                    <span className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground">Channel</span>
                    <select
                        value={channel}
                        onChange={(e) => setChannel(e.target.value)}
                        className="mt-1 w-full h-8 border border-border bg-white text-xs px-2"
                        data-testid="qt-quicklog-channel"
                    >
                        {FOLLOWUP_CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                </label>
            </div>
            <label className="block">
                <span className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground">Note (optional)</span>
                <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    rows={2}
                    placeholder="What did they say?"
                    className="mt-1 w-full border border-border bg-white text-xs px-2 py-1.5 resize-none"
                    data-testid="qt-quicklog-note"
                />
            </label>
            <label className="block">
                <span className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground">Next action (optional)</span>
                <input
                    type="datetime-local"
                    value={nextActionAt}
                    onChange={(e) => setNextActionAt(e.target.value)}
                    className="mt-1 w-full h-8 border border-border bg-white text-xs px-2"
                    data-testid="qt-quicklog-next-action"
                />
            </label>
            <div className="flex items-center gap-2 pt-1">
                <button
                    type="submit"
                    disabled={busy}
                    className="inline-flex items-center gap-1 h-8 px-3 bg-ikb text-white text-[11px] font-mono uppercase tracking-widest hover:bg-ikb/90 disabled:opacity-60"
                    data-testid="qt-quicklog-submit"
                >
                    {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                    Log
                </button>
                <button
                    type="button"
                    onClick={() => { setExpanded(false); setNote(""); setNextActionAt(""); }}
                    className="h-8 px-3 border border-border text-[11px] font-mono uppercase tracking-widest bg-white hover:bg-neutral-50"
                    data-testid="qt-quicklog-cancel"
                >
                    Cancel
                </button>
            </div>
        </form>
    );
}

/** InlineFollowupEditor — swap-in edit form for an existing follow-up.
 *  PATCH /candidates/{cid}/followups/{fid} with the same shape as create. */
function InlineFollowupEditor({ candidateId, entry, index, onDone, onCancel }) {
    const [status, setStatus] = useState(entry.meta?.status || FOLLOWUP_STATUSES[0]);
    const [channel, setChannel] = useState(entry.meta?.channel || "call");
    const [note, setNote] = useState(entry.body || "");
    const [nextActionAt, setNextActionAt] = useState(() => {
        const iso = entry.meta?.next_action_at;
        if (!iso) return "";
        // Convert ISO to `YYYY-MM-DDTHH:mm` for the datetime-local input.
        try { return new Date(iso).toISOString().slice(0, 16); } catch { return ""; }
    });
    const [busy, setBusy] = useState(false);

    async function save() {
        if (busy) return;
        const fid = entry.meta?.followup_id;
        if (!fid) { toast.error("Missing follow-up id"); return; }
        setBusy(true);
        try {
            const payload = { status, channel, note };
            payload.next_action_at = nextActionAt ? new Date(nextActionAt).toISOString() : null;
            await api.patch(`/candidates/${candidateId}/followups/${fid}`, payload);
            toast.success("Follow-up updated");
            onDone?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="border border-ikb/40 bg-ikb/5 p-2 space-y-2" data-testid={`qt-followup-editor-${index}`}>
            <div className="grid grid-cols-2 gap-2">
                <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="h-7 border border-border bg-white text-xs px-1.5"
                    data-testid={`qt-followup-edit-status-${index}`}
                >
                    {FOLLOWUP_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <select
                    value={channel}
                    onChange={(e) => setChannel(e.target.value)}
                    className="h-7 border border-border bg-white text-xs px-1.5"
                    data-testid={`qt-followup-edit-channel-${index}`}
                >
                    {FOLLOWUP_CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
            </div>
            <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                className="w-full border border-border bg-white text-xs px-2 py-1 resize-none"
                data-testid={`qt-followup-edit-note-${index}`}
            />
            <input
                type="datetime-local"
                value={nextActionAt}
                onChange={(e) => setNextActionAt(e.target.value)}
                className="w-full h-7 border border-border bg-white text-xs px-2"
                data-testid={`qt-followup-edit-next-${index}`}
            />
            <div className="flex items-center gap-2">
                <button
                    type="button"
                    onClick={save}
                    disabled={busy}
                    className="inline-flex items-center gap-1 h-7 px-2 bg-ikb text-white text-[10px] font-mono uppercase tracking-widest disabled:opacity-60"
                    data-testid={`qt-followup-edit-save-${index}`}
                >
                    {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Save
                </button>
                <button
                    type="button"
                    onClick={onCancel}
                    className="h-7 px-2 border border-border text-[10px] font-mono uppercase tracking-widest bg-white"
                    data-testid={`qt-followup-edit-cancel-${index}`}
                >
                    Cancel
                </button>
            </div>
        </div>
    );
}

/** InlineNoteEditor — small textarea + save/cancel for editing a note body. */
function InlineNoteEditor({ candidateId, entry, index, onDone, onCancel }) {
    const [body, setBody] = useState(entry.body || "");
    const [busy, setBusy] = useState(false);

    async function save() {
        if (busy) return;
        const nid = entry.meta?.note_id;
        if (!nid) { toast.error("Missing note id"); return; }
        setBusy(true);
        try {
            await api.patch(`/candidates/${candidateId}/notes/${nid}`, { body });
            toast.success("Note updated");
            onDone?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="space-y-2" data-testid={`qt-note-editor-${index}`}>
            <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={3}
                className="w-full border border-border bg-white text-xs px-2 py-1 resize-none"
                data-testid={`qt-note-edit-body-${index}`}
            />
            <div className="flex items-center gap-2">
                <button
                    type="button"
                    onClick={save}
                    disabled={busy}
                    className="inline-flex items-center gap-1 h-7 px-2 bg-ikb text-white text-[10px] font-mono uppercase tracking-widest disabled:opacity-60"
                    data-testid={`qt-note-edit-save-${index}`}
                >
                    {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Save
                </button>
                <button
                    type="button"
                    onClick={onCancel}
                    className="h-7 px-2 border border-border text-[10px] font-mono uppercase tracking-widest bg-white"
                    data-testid={`qt-note-edit-cancel-${index}`}
                >
                    Cancel
                </button>
            </div>
        </div>
    );
}

/** TimelineEntry — one row in the follow-up history. Renders a read-only
 *  view by default and swaps to an inline editor for the current user's
 *  own follow-ups / notes (server-side guards remain authoritative — this
 *  is UX gating, not access control). */
function TimelineEntry({ entry: t, index: i, candidateId, currentUserId, isEmployer, onRefresh }) {
    const [editing, setEditing] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [pinBusy, setPinBusy] = useState(false);

    const isFollowup = t.kind === "followup";
    const isNote = t.kind === "note";
    const ownerId = isFollowup ? t.meta?.created_by : (isNote ? t.meta?.author_id : null);
    const editable = (isFollowup || isNote) && (isEmployer || (ownerId && ownerId === currentUserId));
    const pinned = !!t.meta?.pinned;

    async function togglePin() {
        if (!editable || pinBusy) return;
        setPinBusy(true);
        try {
            if (isFollowup) {
                await api.patch(`/candidates/${candidateId}/followups/${t.meta.followup_id}`, { pinned: !pinned });
            } else {
                await api.patch(`/candidates/${candidateId}/notes/${t.meta.note_id}`, { pinned: !pinned });
            }
            toast.success(pinned ? "Unpinned" : "Pinned to top");
            onRefresh?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setPinBusy(false);
        }
    }

    async function del() {
        if (!editable || deleting) return;
        const kind = isFollowup ? "follow-up" : "note";
        if (!window.confirm(`Delete this ${kind}? This cannot be undone.`)) return;
        setDeleting(true);
        try {
            if (isFollowup) {
                await api.delete(`/candidates/${candidateId}/followups/${t.meta.followup_id}`);
            } else {
                await api.delete(`/candidates/${candidateId}/notes/${t.meta.note_id}`);
            }
            toast.success(`${kind[0].toUpperCase()}${kind.slice(1)} deleted`);
            onRefresh?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setDeleting(false);
        }
    }

    if (editing && isFollowup) {
        return (
            <li className="flex gap-3" data-testid={`qt-timeline-${i}`}>
                <div className="mt-0.5 w-6 h-6 shrink-0 rounded-full bg-neutral-100 border border-border inline-flex items-center justify-center">
                    <KindIcon kind={t.kind} />
                </div>
                <div className="flex-1 min-w-0">
                    <InlineFollowupEditor
                        candidateId={candidateId}
                        entry={t}
                        index={i}
                        onDone={() => { setEditing(false); onRefresh?.(); }}
                        onCancel={() => setEditing(false)}
                    />
                </div>
            </li>
        );
    }
    if (editing && isNote) {
        return (
            <li className="flex gap-3" data-testid={`qt-timeline-${i}`}>
                <div className="mt-0.5 w-6 h-6 shrink-0 rounded-full bg-neutral-100 border border-border inline-flex items-center justify-center">
                    <KindIcon kind={t.kind} />
                </div>
                <div className="flex-1 min-w-0">
                    <InlineNoteEditor
                        candidateId={candidateId}
                        entry={t}
                        index={i}
                        onDone={() => { setEditing(false); onRefresh?.(); }}
                        onCancel={() => setEditing(false)}
                    />
                </div>
            </li>
        );
    }

    return (
        <li className={`flex gap-3 group ${pinned ? "bg-amber-50/60 border-l-4 border-amber-500 -ml-3 pl-3 py-1 rounded-r" : ""}`} data-testid={`qt-timeline-${i}`}>
            <div className="mt-0.5 w-6 h-6 shrink-0 rounded-full bg-neutral-100 border border-border inline-flex items-center justify-center">
                <KindIcon kind={t.kind} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{isFollowup ? "follow-up" : t.kind}</span>
                    {pinned && (
                        <span
                            className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-amber-500 text-amber-700 bg-amber-100 inline-flex items-center gap-1"
                            data-testid={`qt-pinned-badge-${i}`}
                        >
                            <Pin className="w-2.5 h-2.5" /> Pinned
                        </span>
                    )}
                    {isFollowup && t.meta?.status && (
                        <span
                            className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 border ${followupStatusClasses(t.meta.status)}`}
                            data-testid={`qt-followup-status-${i}`}
                        >
                            {t.meta.status}
                        </span>
                    )}
                    {isFollowup && t.meta?.channel && (
                        <span className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground">
                            via {t.meta.channel}
                        </span>
                    )}
                    {t.actor && <span className="text-[11px] font-medium truncate">{t.actor}</span>}
                    <span className="text-[10px] font-mono text-muted-foreground ml-auto">{timeAgo(t.when)}</span>
                    {editable && (
                        <span className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-1">
                            <button
                                type="button"
                                onClick={togglePin}
                                disabled={pinBusy}
                                className={`w-5 h-5 inline-flex items-center justify-center border bg-white disabled:opacity-50 ${pinned ? "border-amber-500 text-amber-700 hover:bg-amber-50" : "border-border hover:bg-amber-50 hover:border-amber-300"}`}
                                data-testid={`qt-timeline-pin-${i}`}
                                title={pinned ? "Unpin" : "Pin to top"}
                            >
                                {pinBusy ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Pin className="w-2.5 h-2.5" />}
                            </button>
                            <button
                                type="button"
                                onClick={() => setEditing(true)}
                                className="w-5 h-5 inline-flex items-center justify-center border border-border bg-white hover:bg-neutral-50"
                                data-testid={`qt-timeline-edit-${i}`}
                                title="Edit"
                            >
                                <Edit2 className="w-2.5 h-2.5" />
                            </button>
                            <button
                                type="button"
                                onClick={del}
                                disabled={deleting}
                                className="w-5 h-5 inline-flex items-center justify-center border border-border bg-white hover:bg-red-50 hover:border-red-300 disabled:opacity-50"
                                data-testid={`qt-timeline-delete-${i}`}
                                title="Delete"
                            >
                                {deleting ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Trash2 className="w-2.5 h-2.5" />}
                            </button>
                        </span>
                    )}
                </div>
                {t.body ? <div className="mt-0.5 text-xs text-foreground/80 whitespace-pre-line line-clamp-4">{t.body}</div> : null}
                {isFollowup && t.meta?.next_action_at && (
                    <div className="mt-1 text-[10px] font-mono text-blue-700 inline-flex items-center gap-1">
                        <CalendarClock className="w-3 h-3" /> Next action: {new Date(t.meta.next_action_at).toLocaleString()}
                    </div>
                )}
            </div>
        </li>
    );
}

export default function QuickTrackDrawer({ candidateId, open, onClose }) {
    const nav = useNavigate();
    const { user } = useAuth() || {};
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const [loading, setLoading] = useState(false);
    const [offerBusy, setOfferBusy] = useState(null); // application_id currently being computed
    const [refreshTick, setRefreshTick] = useState(0);

    useEffect(() => {
        if (!open || !candidateId) return;
        let cancelled = false;
        (async () => {
            setLoading(true); setErr(null);
            try {
                const { data } = await api.get(`/candidates/${candidateId}/quick-track`);
                if (!cancelled) setData(data);
            } catch (e) {
                if (!cancelled) setErr(formatApiError(e));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [candidateId, open, refreshTick]);

    const refresh = () => setRefreshTick((n) => n + 1);

    // Close on Escape
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    // While mounted, mark body so Radix HoverCards (which linger on
    // mouse-hover) are hidden by a CSS rule in index.css. Fixes iter116
    // production bug where the hover card stayed visible under/beside
    // the drawer because the mouse was still over the row.
    useEffect(() => {
        if (!open) return;
        document.body.classList.add("qt-drawer-open");
        return () => document.body.classList.remove("qt-drawer-open");
    }, [open]);

    if (!open) return null;

    /** Instant Offer — POST /candidates/{id}/instant-offer-suggest → navigate
     *  to /candidates/{id}/offers with prefill params. Zero clicks between
     *  triage and offer draft. */
    async function draftInstantOffer(app) {
        if (!candidateId) return;
        setOfferBusy(app.id);
        try {
            const { data } = await api.post(`/candidates/${candidateId}/instant-offer-suggest`, {
                application_id: app.id,
                multiplier: 1.0,
            });
            const params = new URLSearchParams({
                instant_offer: "1",
                suggested_ctc: String(data.suggested_ctc || ""),
                current_ctc: String(data.candidate_current_ctc || ""),
                expected_ctc: String(data.candidate_expected_ctc || ""),
                notice_days: String(data.notice_period_days ?? ""),
                title: `Offer — ${data.job_title || "role"}`,
            });
            onClose?.();
            nav(`/candidates/${candidateId}/offers?${params.toString()}`);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setOfferBusy(null);
        }
    }

    const c = data?.candidate;
    const s = data?.salary;
    const sc = data?.scores;
    const apps = data?.applications || [];
    const interviews = data?.interviews || [];
    const timeline = data?.timeline || [];
    const counts = data?.counts || {};

    return createPortal((
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4" data-testid="quick-track-drawer" role="dialog" aria-modal="true">
            {/* Solid backdrop — deliberately opaque black/70 (no blur) so it
             *  reliably paints over sticky-positioned table cells that create
             *  their own stacking context (iter117 production bug). */}
            <button
                aria-label="Close drawer"
                onClick={onClose}
                className="absolute inset-0 bg-black/70"
                data-testid="quick-track-backdrop"
            />
            {/* Centered modal — max 960px, 88vh so the follow-up history is
             *  visible without scrolling on typical laptop screens. */}
            <div
                onClick={(e) => e.stopPropagation()}
                className="relative bg-neutral-50 border border-border shadow-2xl w-full max-w-[960px] max-h-[88vh] flex flex-col"
            >
                {/* Header */}
                <div className="bg-white border-b border-border px-5 py-3 flex items-center gap-3 shrink-0">
                    <div className="w-8 h-8 rounded-full bg-ikb/10 text-ikb inline-flex items-center justify-center">
                        <Sparkles className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Quick Track</div>
                        <div className="font-semibold text-sm truncate">{c?.name || "Loading…"}</div>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 inline-flex items-center justify-center hover:bg-neutral-100 border border-border"
                        data-testid="quick-track-close"
                        aria-label="Close"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Scrollable body */}
                <div className="flex-1 overflow-y-auto pw-thin-scroll">
                {loading && !data && (
                    <div className="min-h-[300px] flex items-center justify-center text-muted-foreground text-xs">
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading candidate journey…
                    </div>
                )}
                {err && (
                    <div className="m-4 border border-red-300 bg-red-50 p-3 text-xs text-red-800" data-testid="quick-track-error">
                        {err}
                    </div>
                )}

                {data && (
                    <div className="p-4 space-y-4">
                        {/* Candidate summary card */}
                        <div className="border border-border bg-white p-4">
                            <div className="text-xs text-muted-foreground">{c?.current_title || "—"}{c?.current_company ? ` · ${c.current_company}` : ""}</div>
                            <div className="text-sm mt-1">{c?.location || "—"} · {c?.years_of_experience ? `${c.years_of_experience} yrs exp` : "exp unknown"}</div>
                            <div className="mt-2 flex flex-wrap gap-1">
                                {c?.email && <span className="text-[10px] font-mono border border-border px-1.5 py-0.5">{c.email}</span>}
                                {c?.phone && <span className="text-[10px] font-mono border border-border px-1.5 py-0.5">{c.phone}</span>}
                            </div>
                        </div>

                        {/* Scores + Salary side-by-side to save vertical space */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Section title="Scores" icon={Star} testId="quick-track-scores">
                                <div className="grid grid-cols-3 gap-3">
                                    <div>
                                        <div className="text-[10px] font-mono uppercase text-muted-foreground">Latest AI</div>
                                        <div className="font-display font-black text-2xl tabular-nums">{sc?.latest_ai_score ?? "—"}</div>
                                    </div>
                                    <div>
                                        <div className="text-[10px] font-mono uppercase text-muted-foreground">Recruiter</div>
                                        <div className="font-display font-black text-2xl tabular-nums">{sc?.latest_recruiter_score ?? "—"}</div>
                                    </div>
                                    <div>
                                        <div className="text-[10px] font-mono uppercase text-muted-foreground">Avg AI</div>
                                        <div className="font-display font-black text-2xl tabular-nums">{sc?.avg_ai_score ?? "—"}</div>
                                    </div>
                                </div>
                                <div className="mt-3 flex items-center gap-4 text-xs">
                                    <span className="inline-flex items-center gap-1 text-emerald-700"><ThumbsUp className="w-3.5 h-3.5" /> {sc?.votes?.up || 0}</span>
                                    <span className="inline-flex items-center gap-1 text-red-700"><ThumbsDown className="w-3.5 h-3.5" /> {sc?.votes?.down || 0}</span>
                                </div>
                            </Section>

                            <Section title="Salary snapshot" icon={Wallet} testId="quick-track-salary">
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <div className="text-[10px] font-mono uppercase text-muted-foreground">Current CTC</div>
                                        <div className="font-semibold text-lg mt-0.5" data-testid="qt-current-ctc">{inr(s?.current_ctc, s?.currency)}</div>
                                    </div>
                                    <div>
                                        <div className="text-[10px] font-mono uppercase text-muted-foreground">Desired CTC</div>
                                        <div className="font-semibold text-lg mt-0.5" data-testid="qt-desired-ctc">
                                            {s?.desired_ctc_min || s?.desired_ctc_max
                                                ? `${inr(s?.desired_ctc_min, s?.currency)}${s?.desired_ctc_max ? ` – ${inr(s?.desired_ctc_max, s?.currency)}` : ""}`
                                                : "—"}
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-3 flex items-center gap-3 text-xs">
                                    {s?.delta_pct != null && (
                                        <span className={`inline-flex items-center gap-1 font-mono uppercase tracking-widest text-[10px] px-1.5 py-0.5 border ${s.delta_pct > 30 ? "border-amber-500 text-amber-800 bg-amber-50" : s.delta_pct > 0 ? "border-emerald-500 text-emerald-800 bg-emerald-50" : "border-border text-muted-foreground"}`} data-testid="qt-delta">
                                            {s.delta_pct > 0 ? "+" : ""}{s.delta_pct}% expected
                                        </span>
                                    )}
                                    {s?.notice_period_days != null && (
                                        <span className="text-muted-foreground">Notice: {s.notice_period_days}d</span>
                                    )}
                                </div>
                            </Section>
                        </div>

                        {/* Follow-up history — promoted above Applications/Interviews per iter117 user report. */}
                        <Section title={`Follow-up history (${timeline.length})`} icon={MessageSquare} testId="quick-track-timeline">
                            <QuickFollowupForm candidateId={candidateId} onDone={refresh} />
                            <QuickNoteForm candidateId={candidateId} onDone={refresh} />
                            {timeline.length === 0 ? (
                                <div className="text-xs text-muted-foreground">No notes, emails or activity yet.</div>
                            ) : (
                                <ul className="space-y-3 max-h-64 overflow-y-auto pw-thin-scroll pr-1">
                                    {timeline.map((t, i) => (
                                        <TimelineEntry
                                            key={t.meta?.followup_id || t.meta?.note_id || `${t.kind}-${i}`}
                                            entry={t}
                                            index={i}
                                            candidateId={candidateId}
                                            currentUserId={user?.id}
                                            isEmployer={user?.role === "employer"}
                                            onRefresh={refresh}
                                        />
                                    ))}
                                </ul>
                            )}
                        </Section>

                        {/* Applications */}
                        {apps.length > 0 && (
                            <Section title={`Applications (${apps.length})`} icon={ChevronRight} testId="quick-track-apps">
                                <ul className="divide-y divide-border">
                                    {apps.map((a) => (
                                        <li key={a.id} className="py-2" data-testid={`qt-app-${a.id}`}>
                                            <div className="flex items-center justify-between gap-2">
                                                <div className="text-xs font-medium truncate flex-1">{a.job_title}</div>
                                                <span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-border bg-neutral-50 shrink-0">{a.stage || "—"}</span>
                                            </div>
                                            <div className="mt-1 flex items-center gap-3 text-[10px] font-mono text-muted-foreground">
                                                {a.ai_score != null && <span>AI {a.ai_score}</span>}
                                                {a.recruiter_score != null && <span>Rec {a.recruiter_score}</span>}
                                                <span className="opacity-60">{timeAgo(a.applied_at)}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => draftInstantOffer(a)}
                                                    disabled={offerBusy === a.id}
                                                    className="ml-auto inline-flex items-center gap-1 h-6 px-2 border border-emerald-500 text-emerald-800 bg-white hover:bg-emerald-50 text-[10px] font-mono uppercase tracking-widest disabled:opacity-50"
                                                    data-testid={`qt-instant-offer-${a.id}`}
                                                    title="Draft an offer with computed CTC and pre-filled candidate data"
                                                >
                                                    <FileSignature className="w-3 h-3" />
                                                    {offerBusy === a.id ? "…" : "Instant offer"}
                                                </button>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </Section>
                        )}

                        {/* Interviews */}
                        {interviews.length > 0 && (
                            <Section title={`Interviews (${interviews.length})`} icon={CalendarClock} testId="quick-track-interviews">
                                <ul className="divide-y divide-border">
                                    {interviews.map((iv) => (
                                        <li key={iv.id} className="py-2" data-testid={`qt-iv-${iv.id}`}>
                                            <div className="flex items-center justify-between gap-2">
                                                <div className="text-xs truncate flex-1">
                                                    <span className="font-medium">{iv.round_name || `Round ${iv.round_number || iv.round || "—"}`}</span>
                                                    {iv.panel?.length ? <span className="text-muted-foreground"> · {iv.panel.join(", ")}</span> : null}
                                                </div>
                                                <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border shrink-0 ${
                                                    (iv.status || "").toLowerCase() === "completed" ? "border-emerald-500 text-emerald-800 bg-emerald-50" :
                                                    (iv.status || "").toLowerCase() === "cancelled" ? "border-red-400 text-red-700 bg-red-50" :
                                                    "border-blue-400 text-blue-700 bg-blue-50"
                                                }`}>{iv.status || "—"}</span>
                                            </div>
                                            <div className="mt-1 text-[10px] font-mono text-muted-foreground">
                                                {iv.scheduled_at ? timeAgo(iv.scheduled_at) : "—"}
                                                {iv.rating != null ? ` · rated ${iv.rating}` : ""}
                                            </div>
                                            {iv.feedback ? <div className="mt-1 text-xs text-foreground/80 line-clamp-2">{iv.feedback}</div> : null}
                                        </li>
                                    ))}
                                </ul>
                            </Section>
                        )}

                        {/* Footer stats strip */}
                        <div className="grid grid-cols-5 gap-2 pt-2">
                            {[
                                ["Notes", counts.notes || 0],
                                ["Follow-ups", counts.followups || 0],
                                ["Emails", counts.emails || 0],
                                ["SMS/WA", counts.sms || 0],
                                ["IVs", (counts.interviews_scheduled || 0) + (counts.interviews_completed || 0)],
                            ].map(([label, n]) => (
                                <div key={label} className="border border-border bg-white p-2 text-center">
                                    <div className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground">{label}</div>
                                    <div className="font-semibold text-sm tabular-nums">{n}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                </div>
            </div>
        </div>
    ), document.body);
}
